import { collection, addDoc, updateDoc, deleteDoc, doc, Timestamp, setDoc, getDocs, writeBatch, query, where } from 'firebase/firestore';
import { db } from '../firebase'; 
import { uploadImage } from '../utils/imageUpload'; 

// 🕐 Horario distinto según el día del barbero — { "1": { start: "09:00", end: "13:00" }, ... }
// (clave = día de la semana como getDay(): 0=domingo...6=sábado, igual que workingDays).
// Se limpia antes de guardar: solo días válidos y horas con formato HH:mm; un día sin
// ninguna hora cargada no se guarda (= usa el horario general del barbero).
const HORA_VALIDA = /^([01]\d|2[0-3]):[0-5]\d$/;
const NOMBRES_DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const limpiarHorarioPorDia = (raw) => {
  const limpio = {};
  Object.entries(raw || {}).forEach(([dia, h]) => {
    const d = Number(dia);
    if (!Number.isInteger(d) || d < 0 || d > 6 || !h) return;
    const start = HORA_VALIDA.test(h.start || '') ? h.start : '';
    const end = HORA_VALIDA.test(h.end || '') ? h.end : '';
    if (start || end) limpio[String(d)] = { start, end };
  });
  return limpio;
};

const useCrudActions = (setLoading, addToast, reloadData) => {

  // --- LOCALES ---
  const handleSaveLocation = async (editingId, locationInfo, file, currentLocations, setSelectedAdminLocationId) => {
    setLoading(true);
    try {
      let imageUrl = locationInfo.image || 'https://cdn-icons-png.flaticon.com/512/3655/3655587.png';
      if (file) imageUrl = await uploadImage(file);

      let positionToSave = 0;
      if (editingId) {
        const existing = currentLocations.find(l => l.id === editingId);
        positionToSave = existing?.position || 0;
      } else {
        positionToSave = currentLocations.length > 0 ? Math.max(...currentLocations.map(l => l.position || 0)) + 1 : 1;
      }

      const data = {
        name: locationInfo.name,
        address: locationInfo.address,
        mapUrl: locationInfo.mapUrl || '',
        googleReviewsUrl: locationInfo.googleReviewsUrl || '',
        image: imageUrl,
        position: positionToSave,
        createdAt: Timestamp.now()
      };

      if (editingId) {
        await updateDoc(doc(db, 'locations', editingId), data);
        await setDoc(doc(db, "locations", editingId, "settings", "general"), { 
            shopName: data.name, 
            address: data.address,
            mapUrl: data.mapUrl 
        }, { merge: true });
        addToast("Actualizado", "Local guardado.", "success");
      } else {
        const newRef = await addDoc(collection(db, 'locations'), data);
        const defaultSettings = {
           shopName: data.name, address: data.address, mapUrl: data.mapUrl || '',
           openDays: [1, 2, 3, 4, 5, 6], openTime: '09:00', closeTime: '20:00', 
           lunchStart: '12:00', lunchEnd: '13:00', requirePayment: false, logoUrl: imageUrl
        };
        await setDoc(doc(db, "locations", newRef.id, "settings", "general"), defaultSettings);
        if(setSelectedAdminLocationId) setSelectedAdminLocationId(newRef.id);
        addToast("Creado", "Nuevo local listo.", "success");
      }
      await reloadData(); 
      return true; 
    } catch (error) {
      console.error(error);
      addToast("Error", "No se pudo guardar.", "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocation = async (id, selectedAdminLocationId, setSelectedAdminLocationId) => {
    if (!window.confirm("⚠️ ¿Eliminar local? Se perderán sus datos.")) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'locations', id));
      if (selectedAdminLocationId === id) setSelectedAdminLocationId(null);
      addToast("Eliminado", "Local eliminado.", "success");
      await reloadData();
    } catch (error) {
      addToast("Error", "No se pudo eliminar.", "error");
    } finally {
      setLoading(false);
    }
  };

  // --- SERVICIOS ---
  const handleSaveService = async (locationId, currentService, file) => {
    if (!locationId) return;
    setLoading(true);
    try {
      let finalUrl = currentService.image;
      if (file) finalUrl = await uploadImage(file);

      const nuevaDuracion = Number(currentService.duration);

      const data = {
        name: currentService.name,
        duration: nuevaDuracion,
        price: Number(currentService.price),
        // 🎫 A pedido: precio de socio, opcional — null si lo dejaron
        // vacío (sin descuento de membresía en este servicio puntual),
        // el número cargado si lo completaron. Usado en ClientView
        // (precio tachado + precio de socio) y en App.js
        // (effectiveTotalPrice), sin necesitar ningún cambio ahí.
        memberPrice: (currentService.memberPrice !== undefined && currentService.memberPrice !== null && currentService.memberPrice !== '')
          ? Number(currentService.memberPrice)
          : null,
        description: currentService.description || '',
        category: currentService.category || 'Cabello',
        image: finalUrl || ''
      };

      if (currentService.id) {
        await updateDoc(doc(db, 'locations', locationId, 'services', currentService.id), data);

        // 🔧 A pedido — encontré dos problemas acá:
        // 1) La consulta traía TODAS las reservas de la sucursal, de toda
        //    la historia (incluidas completadas/canceladas de hace meses)
        //    — no tiene sentido cambiar retroactivamente la duración de
        //    un turno que ya pasó, y además eso infla mucho la cantidad
        //    de documentos a tocar.
        // 2) Firestore permite máximo 500 operaciones por lote — si había
        //    más de 500 reservas coincidiendo, el batch.commit() fallaba
        //    entero (todo o nada) y NO actualizaba ninguna, sin ningún
        //    aviso claro de por qué. Ahora se filtra por estado (solo
        //    pending/confirmed, los turnos que todavía van a pasar) y se
        //    divide en lotes de a 500 si hiciera falta.
        const q = query(
          collection(db, 'bookings'),
          where('locationId', '==', locationId),
          where('status', 'in', ['pending', 'confirmed'])
        );
        const snapshot = await getDocs(q);

        const docsAActualizar = [];
        snapshot.forEach((reservaDoc) => {
            const reservaData = reservaDoc.data();
            if (reservaData.services && Array.isArray(reservaData.services)) {
                const tieneElServicio = reservaData.services.some(s => s.name === currentService.name);
                if (tieneElServicio) {
                    const serviciosActualizados = reservaData.services.map(s =>
                        s.name === currentService.name ? { ...s, duration: nuevaDuracion } : s
                    );
                    const nuevoTiempoTotal = serviciosActualizados.reduce((suma, s) => suma + Number(s.duration || 0), 0);
                    docsAActualizar.push({ ref: reservaDoc.ref, services: serviciosActualizados, totalDuration: nuevoTiempoTotal });
                }
            }
        });

        let reservasActualizadas = 0;
        // Lotes de 500 (el máximo que permite Firestore por batch)
        for (let i = 0; i < docsAActualizar.length; i += 500) {
            const lote = docsAActualizar.slice(i, i + 500);
            const batch = writeBatch(db);
            lote.forEach(({ ref, services: serviciosActualizados, totalDuration }) => {
                batch.update(ref, { services: serviciosActualizados, totalDuration });
            });
            await batch.commit();
            reservasActualizadas += lote.length;
        }

        if (reservasActualizadas > 0) {
            console.log(`✅ ¡Éxito! Se actualizaron ${reservasActualizadas} reservas a ${nuevaDuracion} min.`);
            addToast("Actualizado", `Servicio actualizado — se ajustó la duración en ${reservasActualizadas} turno(s) pendiente(s)/confirmado(s).`, "success");
        } else {
            addToast("Actualizado", "Servicio modificado. No había turnos pendientes o confirmados usando este servicio para ajustar.", "success");
        }
      } else {
        await addDoc(collection(db, 'locations', locationId, 'services'), data);
        addToast("Guardado", "Servicio creado.", "success");
      }
      
      await reloadData(); 
      return true;
    } catch (error) {
      console.error(error);
      addToast("Error", "No se pudo guardar.", "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (locationId, serviceId) => {
    if (!locationId || !window.confirm('⚠️ ¿Eliminar servicio?')) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'locations', locationId, 'services', serviceId));
      addToast("Eliminado", "Servicio borrado.", "success");
      await reloadData();
    } catch (e) { addToast("Error", "No se pudo eliminar.", "error"); }
    finally { setLoading(false); }
  };

  // --- BARBEROS ---
  const handleSaveBarber = async (locationId, currentBarber, file, allBarbers, categories, companyId = null) => {
    if (!locationId) return;

    // 🕐 Si usa horario distinto según el día, ningún día puede terminar antes de empezar.
    const horarioPorDia = limpiarHorarioPorDia(currentBarber.workSchedule);
    if (currentBarber.hasVariableHours) {
      const diaInvalido = Object.entries(horarioPorDia).find(([, h]) => h.start && h.end && h.start >= h.end);
      if (diaInvalido) {
        addToast("Horario inválido", `El horario del ${NOMBRES_DIAS[Number(diaInvalido[0])]} termina antes de empezar.`, "error");
        return;
      }
    }

    // 🍽️ Almuerzo propio: o los dos horarios o ninguno, y que termine después de empezar.
    const lunchStart = currentBarber.lunchStart || '';
    const lunchEnd = currentBarber.lunchEnd || '';
    if ((lunchStart && !lunchEnd) || (!lunchStart && lunchEnd)) {
      addToast("Almuerzo incompleto", "Cargá la hora de inicio y de fin del almuerzo (o dejá los dos vacíos).", "error");
      return;
    }
    if (lunchStart && lunchEnd && lunchStart >= lunchEnd) {
      addToast("Almuerzo inválido", "El almuerzo termina antes de empezar.", "error");
      return;
    }

    setLoading(true);
    try {
      let imageUrl = currentBarber.image;
      if (file) {
          imageUrl = await uploadImage(file);
      } else if (!imageUrl && !currentBarber.id) {
          imageUrl = '/images/default-barber.png';
      }

      const newNumericId = allBarbers.length > 0 ? Math.max(...allBarbers.map(b => b.id)) + 1 : 1;
      const finalId = currentBarber.id ? Number(currentBarber.id) : newNumericId;

      // Usamos currentBarber en lugar de barberData
      const dataToSave = {
          name: currentBarber.name,
          specialty: currentBarber.specialty || '',
          // 🔧 El selector del modal muestra "Servicio General" cuando no hay nada elegido — se guarda
          // exactamente eso (antes se guardaba 'Barbero', y no se veía igual que lo elegido).
          category: currentBarber.category || 'Servicio General',
          // 🏷️ Cargo que ve el cliente (ej: "Manicurista"). '' = sin cargo propio → se muestra
          // lo de siempre (ver ClientView). Se guarda aparte de "category" (Nivel de Servicio).
          title: String(currentBarber.title || '').trim().slice(0, 40),
          commissionRate: Number(currentBarber.commissionRate) || 0,
          productCommissionRate: Number(currentBarber.productCommissionRate) || 0,
          allowedCategories: currentBarber.allowedCategories || categories.map(c => c.name),
          image: imageUrl,
          id: finalId,
          ...(companyId && { companyId: companyId }),
          
          // 🔥 AQUÍ ESTÁ LA LÍNEA MÁGICA PARA GUARDAR LA DESCRIPCIÓN 🔥
          experience: currentBarber.experience || '', 

          // 📅 Días de trabajo — [] = sin restricción propia (trabaja
          // cualquier día que la barbería esté abierta). Antes este
          // campo directamente NO se guardaba nunca, aunque se marcara
          // bien en el modal — por eso "no guardaba" pase lo que pase.
          workingDays: Array.isArray(currentBarber.workingDays) ? currentBarber.workingDays : [],

          // 💰 Forma de pago — 'commission' (default) o 'salary'. Mismo
          // problema que workingDays: no se estaba guardando nunca.
          paymentType: currentBarber.paymentType === 'salary' ? 'salary' : 'commission',
          fixedSalary: Number(currentBarber.fixedSalary) || 0,

          // 🕐 Horario propio — '' = sin restricción propia (disponible
          // todo el horario de la barbería). Mismo criterio que
          // workingDays: si está vacío, no limita nada.
          workStartTime: currentBarber.workStartTime || '',
          workEndTime: currentBarber.workEndTime || '',

          // 🕐 A pedido: horario DISTINTO según el día. Si hasVariableHours está activado, el
          // horario de cada día de workSchedule reemplaza al de arriba (workStartTime/EndTime)
          // SOLO para ese día; un día sin horario cargado usa el de arriba. Se guarda el mapa
          // aunque esté desactivado, para no perder lo cargado si se vuelve a activar.
          hasVariableHours: !!currentBarber.hasVariableHours,
          workSchedule: horarioPorDia,

          // 🍽️ Almuerzo propio del barbero — '' = sin almuerzo propio. Se
          // bloquea en la agenda todos los días (ver isBlockFree en App.js).
          lunchStart,
          lunchEnd,

          // ⏱️ Duración personalizada por servicio — SOLO para este
          // barbero puntual. Es un mapa { serviceId: minutos } — si un
          // servicio no aparece acá, usa la duración estándar definida
          // en el servicio mismo (no hace falta duplicar nada). Ej: un
          // barbero que tarda 45 min en un corte que a otros les toma 30.
          serviceDurationOverrides: currentBarber.serviceDurationOverrides || {},

          // 💈 Comisión personalizada por servicio — SOLO para este
          // barbero. { idServicio: % }; sin valor usa su % general
          // (ver utils/comisiones.js).
          serviceCommissionOverrides: currentBarber.serviceCommissionOverrides || {},

          // 💰 A pedido: requiere seña por transferencia para confirmar
          // turno — false por default, se activa puntualmente para
          // barberos con problema de clientes que no asisten.
          requiereSeniaTransferencia: !!currentBarber.requiereSeniaTransferencia,

          // 🚶 Atiende por orden de llegada (sin cita) — si es true, NO se
          // muestra en la reserva online del cliente (ver ClientView).
          walkInOnly: !!currentBarber.walkInOnly,
      };

      if (currentBarber.docId) {
          await updateDoc(doc(db, 'locations', locationId, 'barbers', currentBarber.docId), dataToSave);
          addToast("Actualizado", "Barbero modificado.", "success");
      } else {
          await addDoc(collection(db, 'locations', locationId, 'barbers'), dataToSave);
          addToast("Guardado", "Barbero agregado.", "success");
      }
        
      await reloadData();
      return true;
    } catch (error) {
        console.error("Error al guardar:", error);
        addToast("Error", "No se pudo guardar.", "error");
        return false;
    } finally {
        setLoading(false);
    }
  };

  const handleDeleteBarber = async (locationId, barberDocId) => {
     if (!locationId || !window.confirm('⚠️ ¿Eliminar barbero?')) return;
     setLoading(true);
     try {
       await deleteDoc(doc(db, 'locations', locationId, 'barbers', barberDocId));

       // 🔒 A pedido — bug encontrado: el registro de acceso del barbero
       // (colección "users" — vincula su correo con este barberId, y es
       // lo que Firebase Authentication usa para dejarlo entrar) vivía
       // aparte de este documento y nunca se borraba con él. El barbero
       // "eliminado" seguía pudiendo iniciar sesión con su cuenta vieja
       // sin ningún candado. Ahora se busca y se borra ese registro acá
       // mismo, junto con el perfil.
       try {
         const qUsuariosVinculados = query(collection(db, 'users'), where('barberId', '==', barberDocId));
         const usuariosSnap = await getDocs(qUsuariosVinculados);
         for (const userDoc of usuariosSnap.docs) {
           await deleteDoc(doc(db, 'users', userDoc.id));
         }
       } catch (eUsers) {
         console.error("No se pudo borrar el registro de acceso del barbero:", eUsers);
       }

       addToast("Eliminado", "Barbero eliminado.", "success");
       await reloadData();
     } catch (e) { addToast("Error", "No se pudo eliminar.", "error"); }
     finally { setLoading(false); }
  };

  return {
    handleSaveLocation, handleDeleteLocation,
    handleSaveService, handleDeleteService,
    handleSaveBarber, handleDeleteBarber
  };
};

export default useCrudActions;